import { ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { AuthContext, AuthState } from './auth-context';
import { setTokenGetter } from '@/services/api/base';

/** OKTA-backed auth provider (used only when OKTA is configured). */
export function OktaAuthProvider({ children }: { children: ReactNode }) {
  const { oktaAuth, authState } = useOktaAuth();
  const isAuthenticated = authState?.isAuthenticated ?? false;

  // Wire the API client to pull the current access token.
  useEffect(() => {
    setTokenGetter(() => oktaAuth.getAccessToken() ?? null);
    return () => setTokenGetter(() => null);
  }, [oktaAuth]);

  const login = useCallback(() => {
    // Return the promise so the Login page can await it and surface failures instead of
    // silently parking on the spinner.
    return oktaAuth.signInWithRedirect();
  }, [oktaAuth]);

  const logout = useCallback(() => {
    void oktaAuth.signOut();
  }, [oktaAuth]);

  const actor = useMemo(() => {
    const claims = authState?.idToken?.claims as { email?: string; name?: string } | undefined;
    return claims?.email || claims?.name || 'okta-user';
  }, [authState]);

  const value: AuthState = {
    isAuthenticated,
    authEnabled: true,
    loading: !authState,
    actor,
    login,
    logout,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Local no-auth provider (OKTA not configured): always "authenticated", no token. */
export function LocalAuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    setTokenGetter(() => null);
  }, []);
  const value: AuthState = {
    isAuthenticated: true,
    authEnabled: false,
    loading: false,
    actor: 'local-operator',
    login: () => {},
    logout: () => {},
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
